// ============================================================
// pages/BookingPage.jsx — Connected to Firebase
// ============================================================

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useProducts, useBusinessSettings } from "../hooks/useFirestore";
import { BookingService, PromotionService } from "../services/firebaseServices";

const getProductPrice = (product, selectedSize) => {
  if (product.pricingType === "fixed") return product.promoPrice || product.fixedPrice;
  if (product.pricingType === "per_size") {
    const size = product.sizes?.find((s) => s.label === selectedSize);
    return size ? (size.promoPrice || size.price) : 0;
  }
  if (product.pricingType === "range") return product.rangeMin;
  return 0;
};

export default function BookingPage({ showToast, preselectedProduct, preselectedSize, cart, setCart, bookingForm, setBookingForm }) {
  const { user, profile } = useAuth();
  const { products } = useProducts();
  const { settings } = useBusinessSettings();

  // Local product selector (resets on nav — that's fine)
  const [productId, setProductId] = useState(preselectedProduct?.id || "");
  const [size, setSize] = useState(preselectedSize || preselectedProduct?.sizes?.[0]?.label || "");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  const [promoResult, setPromoResult] = useState(null);
  const [slotStatus, setSlotStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingSlot, setCheckingSlot] = useState(false);

  const selectedProduct = products.find((p) => p.id === productId);

  // Check slot availability
  useEffect(() => {
    if (bookingForm.pickupDate && bookingForm.pickupTime) {
      setCheckingSlot(true);
      BookingService.checkAvailability(bookingForm.pickupDate, bookingForm.pickupTime)
        .then(setSlotStatus)
        .catch(() => setSlotStatus(null))
        .finally(() => setCheckingSlot(false));
    }
  }, [bookingForm.pickupDate, bookingForm.pickupTime]);

  const updateBooking = (key, val) => setBookingForm((prev) => ({ ...prev, [key]: val }));

  // Add to cart
  const addToCart = () => {
    if (!selectedProduct) return;
    if (selectedProduct.pricingType === "per_size" && !size) {
      showToast("Please select a size", "error");
      return;
    }
    const price = getProductPrice(selectedProduct, size);
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      pricingType: selectedProduct.pricingType,
      selectedSize: size || null,
      unitPrice: price,
      quantity,
      subtotal: price * quantity,
      specialNotes: notes,
    };
    setCart((prev) => [...prev, item]);
    setProductId("");
    setSize("");
    setQuantity(1);
    setNotes("");
    showToast(`${selectedProduct.name} added to cart!`, "success");
  };

  const removeFromCart = (itemId) => setCart((prev) => prev.filter((i) => i.id !== itemId));

  // Promo code
  const applyPromo = async () => {
    if (!bookingForm.promoCode?.trim()) return;
    try {
      const result = await PromotionService.validateCode(bookingForm.promoCode);
      setPromoResult(result);
      showToast(result.valid ? "Promo code applied!" : result.reason, result.valid ? "success" : "error");
    } catch (err) {
      showToast("Failed to validate promo code", "error");
    }
  };

  // Totals
  const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  const loyaltyDiscount = profile?.loyaltyTier
    ? { bronze: 5, silver: 8, gold: 10, platinum: 15 }[profile.loyaltyTier] || 0 : 0;
  const loyaltyDiscountAmount = Math.round(subtotal * loyaltyDiscount / 100);
  let promoDiscount = 0;
  if (promoResult?.valid) {
    const promo = promoResult.promotion;
    promoDiscount = promo.discountType === "percentage"
      ? Math.round(subtotal * promo.discountValue / 100)
      : promo.discountValue || 0;
  }
  const totalDiscount = Math.max(loyaltyDiscountAmount, promoDiscount);
  const deliveryFee = bookingForm.deliveryMethod === "delivery" ? 100 : 0;
  const total = Math.max(0, subtotal - totalDiscount + deliveryFee);

  // Submit
  const handleSubmit = async () => {
    if (cart.length === 0) { showToast("Please add items to your cart", "error"); return; }
    if (!bookingForm.pickupDate || !bookingForm.pickupTime) { showToast("Please select a date and time", "error"); return; }
    if (slotStatus && !slotStatus.available) { showToast("Selected slot is not available", "error"); return; }

    setSubmitting(true);
    try {
      await BookingService.create({
        items: cart.map(({ id, ...rest }) => rest),
        customerName: profile?.displayName || user?.displayName || "Guest",
        customerEmail: profile?.email || user?.email || "",
        customerPhone: profile?.phone || "",
        pickupDate: new Date(bookingForm.pickupDate),
        pickupTime: bookingForm.pickupTime,
        deliveryMethod: bookingForm.deliveryMethod,
        deliveryAddress: bookingForm.deliveryMethod === "delivery" ? bookingForm.deliveryAddress : null,
        subtotal, discount: totalDiscount,
        discountReason: totalDiscount === loyaltyDiscountAmount
          ? `${profile?.loyaltyTier} tier ${loyaltyDiscount}% discount`
          : promoResult?.promotion?.title || null,
        deliveryFee, total,
      });
      showToast("Booking submitted! We'll confirm it shortly.", "success");
      setCart([]);
      setBookingForm({ pickupDate: "", pickupTime: "", deliveryMethod: "pickup", deliveryAddress: "", promoCode: "" });
      setPromoResult(null);
    } catch (err) {
      showToast("Failed to submit booking. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Time slots
  const getTimeSlots = () => {
    if (!settings?.workingHours || !bookingForm.pickupDate) return [];
    const day = new Date(bookingForm.pickupDate).toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const hours = settings.workingHours[day];
    if (!hours?.isOpen) return [];
    const [startH] = hours.open.split(":").map(Number);
    const [endH] = hours.close.split(":").map(Number);
    return Array.from({ length: endH - startH }, (_, i) => `${(startH + i).toString().padStart(2, "0")}:00`);
  };
  const timeSlots = getTimeSlots();

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Book an Order</h2>
          <p>Select your treats and schedule a pickup or delivery</p>
        </div>
      </div>

      <div className="two-col">
        {/* Left: Product selector */}
        <div>
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Select Product</h3>

            <div className="form-group">
              <label className="form-label">Product</label>
              <select className="form-select" value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Choose a product...</option>
                {products.filter((p) => p.available).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {selectedProduct?.pricingType === "per_size" && (
              <div className="form-group">
                <label className="form-label">Select Size</label>
                <div className="size-picker">
                  {selectedProduct.sizes.map((s) => (
                    <button key={s.label} className={`size-btn ${size === s.label ? "selected" : ""}`}
                      onClick={() => setSize(s.label)}>
                      {s.label}<span className="size-price">Rs {s.promoPrice || s.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedProduct?.pricingType === "range" && (
              <div style={{ background: "rgba(196,136,58,0.08)", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.88rem" }}>
                Custom pricing: Rs {selectedProduct.rangeMin} — Rs {selectedProduct.rangeMax}. Final price confirmed after consultation.
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input className="form-input" type="number" min="1" max="20" value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} style={{ maxWidth: 120 }} />
            </div>

            <div className="form-group">
              <label className="form-label">Special Notes</label>
              <textarea className="form-textarea" rows="2" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. 'Write Happy Birthday on cake'" />
            </div>

            <button className="btn btn-primary" onClick={addToCart} disabled={!selectedProduct}
              style={{ width: "100%", justifyContent: "center" }}>
              Add to Cart
            </button>
          </div>
        </div>

        {/* Right: Cart + Schedule + Place Order */}
        <div>
          <div className="cart-panel" style={{ position: "sticky", top: 90 }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
              Your Cart ({cart.length})
            </h3>

            {cart.length === 0 ? (
              <div className="empty-state" style={{ padding: "2rem 0" }}><p>Your cart is empty</p></div>
            ) : (
              <>
                {cart.map((item) => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-info">
                      <div className="cart-item-name">{item.productName}</div>
                      {item.selectedSize && <div className="cart-item-size">Size: {item.selectedSize}</div>}
                      <div className="cart-item-size">Qty: {item.quantity}</div>
                    </div>
                    <span className="cart-item-price">Rs {item.subtotal}</span>
                    <button className="cart-item-remove" onClick={() => removeFromCart(item.id)}>✕</button>
                  </div>
                ))}

                <div className="divider" />

                {/* Promo code */}
                <div className="form-group">
                  <label className="form-label">Promo Code</label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <input className="form-input" style={{ flex: 1 }} value={bookingForm.promoCode || ""}
                      onChange={(e) => updateBooking("promoCode", e.target.value.toUpperCase())} placeholder="Enter code" />
                    <button className="btn btn-outline btn-sm" onClick={applyPromo}>Apply</button>
                  </div>
                  {promoResult?.valid && (
                    <div style={{ color: "var(--sage-dark)", fontSize: "0.82rem", marginTop: 4 }}>✓ {promoResult.promotion.title}</div>
                  )}
                </div>

                {/* Totals */}
                <div style={{ fontSize: "0.88rem", color: "var(--cocoa)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span>Subtotal</span><span>Rs {subtotal}</span>
                  </div>
                  {totalDiscount > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "var(--sage-dark)" }}>
                      <span>Discount</span><span>-Rs {totalDiscount}</span>
                    </div>
                  )}
                  {deliveryFee > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Delivery</span><span>Rs {deliveryFee}</span>
                    </div>
                  )}
                </div>
                <div className="cart-total"><span>Total</span><span>Rs {total}</span></div>

                <div className="divider" />

                {/* Schedule & Delivery — inside cart panel */}
                <h4 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "0.75rem", fontSize: "1rem" }}>
                  Schedule & Delivery
                </h4>

                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                    <label className="form-label">Date</label>
                    <input className="form-input" type="date" value={bookingForm.pickupDate}
                      onChange={(e) => updateBooking("pickupDate", e.target.value)}
                      min={new Date(Date.now() + 86400000).toISOString().split("T")[0]} />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                    <label className="form-label">Time</label>
                    <select className="form-select" value={bookingForm.pickupTime}
                      onChange={(e) => updateBooking("pickupTime", e.target.value)}>
                      <option value="">Select...</option>
                      {timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                {bookingForm.pickupDate && bookingForm.pickupTime && (
                  <div style={{
                    padding: "0.5rem 0.75rem", borderRadius: 8, marginBottom: "0.75rem", fontSize: "0.82rem", fontWeight: 600,
                    background: checkingSlot ? "#FFF3CD" : slotStatus?.available ? "#D4EDDA" : "#F8D7DA",
                    color: checkingSlot ? "#856404" : slotStatus?.available ? "#155724" : "#721C24",
                  }}>
                    {checkingSlot ? "Checking..." : slotStatus?.available ? `Available! ${slotStatus.slotsRemaining} slot(s)` : slotStatus?.reason || "Unavailable"}
                  </div>
                )}

                {timeSlots.length === 0 && bookingForm.pickupDate && (
                  <div style={{ padding: "0.5rem 0.75rem", borderRadius: 8, background: "#F8D7DA", color: "#721C24", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
                    We're closed on this day.
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Delivery</label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className={`filter-btn ${bookingForm.deliveryMethod === "pickup" ? "active" : ""}`}
                      onClick={() => updateBooking("deliveryMethod", "pickup")}>Pickup (Free)</button>
                    <button className={`filter-btn ${bookingForm.deliveryMethod === "delivery" ? "active" : ""}`}
                      onClick={() => updateBooking("deliveryMethod", "delivery")}>Delivery (Rs 100)</button>
                  </div>
                </div>

                {bookingForm.deliveryMethod === "delivery" && (
                  <div className="form-group">
                    <label className="form-label">Delivery Address</label>
                    <textarea className="form-textarea" rows="2" value={bookingForm.deliveryAddress}
                      onChange={(e) => updateBooking("deliveryAddress", e.target.value)} placeholder="Full delivery address" />
                  </div>
                )}

                <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || cart.length === 0}
                  style={{ width: "100%", marginTop: "0.75rem", justifyContent: "center" }}>
                  {submitting ? "Submitting..." : `Place Order — Rs ${total}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}